/**
 * Redaction for session-replay console capture.
 *
 * The console capture plugin (consoleRecordPlugin.ts) records every
 * `console.log/info/warn/error` argument and uploads them as part of the
 * recording payload. Developers occasionally log payment objects, JWTs,
 * cookies, and other secrets during debugging — once captured, these end
 * up in long-lived recording chunks that anyone with replay-viewer access
 * can read.
 *
 * This module replaces sensitive substrings with `[REDACTED]` before
 * capture. It is intentionally conservative — false positives (over-
 * redaction of similar-shaped strings) are preferable to false negatives
 * (a real secret leaking through).
 *
 * Three layers:
 *   1. String redaction (`redactString`): scans a single string for
 *      JWT-shaped tokens, long digit runs (potential card/account
 *      numbers), and SSN or routing-number shapes. The recorder in
 *      lib/sessionReplay.ts also applies it to page text and input values
 *      that are not otherwise masked (OWA-877).
 *   2. Object redaction (`redactValue`): walks a JSON-able value and
 *      replaces values under sensitive key names (case-insensitive).
 *   3. The exported `redactConsoleArg` orchestrates both for a single
 *      console argument.
 *
 * Caller (consoleRecordPlugin.ts) wires this into `serializeArg` so the
 * captured payload is always pre-redacted.
 *
 * @module client/SessionReplay/consoleRedactor
 */

const REDACTED = '[REDACTED]';

/**
 * Object property names whose value should always be redacted, regardless of
 * the value's shape. Match is case-insensitive and uses substring containment
 * — `passwordConfirm`, `oldPassword`, `apiKey2`, `customerToken` all match.
 */
const SENSITIVE_KEY_PATTERNS: readonly string[] = [
    'password',
    'passwd',
    'secret',
    'token',
    'apikey',
    'api_key',
    'authorization',
    'auth_header',
    'bearer',
    'cookie',
    'session_id',
    'sessionid',
    'cvv',
    'cvc',
    'ssn',
    'credit_card',
    'creditcard',
    'cc_number',
    'ccnumber',
    'card_number',
    'cardnumber',
    'bank_account',
    'bankaccount',
    'routing_number',
    'routingnumber',
    'account_number',
    'accountnumber',
    'pin',
    'private_key',
    'privatekey',
];

const SENSITIVE_KEY_SET = SENSITIVE_KEY_PATTERNS.map(p => p.toLowerCase());

/**
 * Returns true if `key` (case-insensitive) contains any sensitive substring.
 * Used to decide whether to redact an object property's value.
 */
export function isSensitiveKey(key: string): boolean {
    if (typeof key !== 'string' || key.length === 0) return false;
    const lower = key.toLowerCase();
    for (const pattern of SENSITIVE_KEY_SET) {
        if (lower.includes(pattern)) return true;
    }
    return false;
}

/**
 * JWT shape: three base64url segments separated by dots, leading "eyJ"
 * (the base64 of `{"`). Conservative — we don't validate the segments;
 * any "eyJ...."-shaped token gets redacted.
 *
 * Anchored to non-token boundaries so URLs like `?eyJ=foo` don't match
 * just on the prefix.
 */
const JWT_RE = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\b/g;

/**
 * Long contiguous digit runs (with optional spaces/dashes between) that
 * look like card or account numbers. 12+ digits to reduce false positives
 * on UUIDs, timestamps, and ID strings.
 *
 * The regex matches the visible digit-run including separators; we redact
 * the entire match.
 */
// eslint-disable-next-line security/detect-unsafe-regex -- bounded quantifier {11,} with a non-optional trailing \d; no nested unbounded repetition, so no catastrophic backtracking. Runs only over local console-log strings.
const LONG_DIGIT_RE = /(?:\d[ -]?){11,}\d/g;

/**
 * OWA-877. US Social Security numbers (and ITINs, which share the shape) as
 * 3-2-4 digits with any single non-alphanumeric separator between the groups
 * (dash, space, dot, slash, comma, or a mix), and any standalone 9-digit
 * number, which covers an undashed SSN and a 9-digit ABA routing number
 * alike. The 3-2-4 grouping is distinctive enough to accept any separator; a
 * 9-digit run with separators is not matched, because that would catch
 * amounts such as `$1,234,567.89` and rows of small numbers.
 *
 * Boundaries keep false positives low: the match may not touch a letter or
 * digit, and may not continue a longer dash chain or a decimal. So phone
 * numbers (3-3-4), dates, ZIP+4, 3-3-3 groupings, 8, 10 and 11 digit numbers,
 * and `0.123456789` are left alone; `$123,456,789` has no 9-digit run. A comma
 * is deliberately not a boundary, so a comma-separated list of SSNs is caught;
 * the cost is that `0,123456789` (a decimal comma) is redacted.
 *
 * The routing checksum and the SSN validity rules are deliberately not used.
 * A checksum alone would miss every undashed SSN, and the SSN rules would
 * exclude ITINs (area 9xx) and real routing numbers such as 021000021 (group
 * 00). With both, almost no 9-digit number is spared, so the extra code buys
 * nothing. The accepted cost is that a standalone 9-digit record or reference
 * number is redacted too.
 *
 * The leading boundary is a captured prefix (put back by the replacement)
 * rather than a lookbehind, because Safari before 16.4 cannot parse lookbehind
 * and the recorder imports this module at app start. The prefix is the start
 * of the string, a character that is not a letter, digit, `-` or `.`, or a
 * `-` or `.` that does not follow a digit.
 */
const SSN_ROUTING_RE = /(^|[^A-Za-z0-9.-]|[^\d][.-]|^[.-])(?:\d{3}[^A-Za-z0-9]\d{2}[^A-Za-z0-9]\d{4}|\d{9})(?![A-Za-z0-9]|[.-]\d)/g;

/**
 * Replace JWT, long-digit-run, and SSN or routing-number patterns inside a
 * string with [REDACTED]. Returns the original string when no patterns match.
 */
export function redactString(value: string): string {
    if (typeof value !== 'string' || value.length === 0) return value;
    let out = value.replace(JWT_RE, REDACTED);
    out = out.replace(LONG_DIGIT_RE, REDACTED);
    out = out.replace(SSN_ROUTING_RE, `$1${REDACTED}`);
    return out;
}

/**
 * Walk a JSON-able value and redact:
 *   - Property values whose KEY matches a sensitive pattern (substring,
 *     case-insensitive). Replaced with the literal string `[REDACTED]`.
 *   - String values (anywhere in the structure) that contain JWT or
 *     long-digit-run patterns.
 *
 * The walker is depth-bounded to defend against pathological cyclic graphs
 * and runaway recursion. Cycles trigger a `[Circular]` marker.
 *
 * Returns a NEW value — does not mutate the input.
 */
export function redactValue(value: unknown, depth = 0, seen?: WeakSet<object>): unknown {
    if (depth > 8) return '[MaxDepth]';

    if (value === null || value === undefined) return value;

    if (typeof value === 'string') return redactString(value);

    if (typeof value !== 'object') return value;

    // Track visited objects/arrays to prevent infinite recursion on cycles.
    const visited = seen ?? new WeakSet<object>();
    if (visited.has(value as object)) return '[Circular]';
    visited.add(value as object);

    if (Array.isArray(value)) {
        return value.map(item => redactValue(item, depth + 1, visited));
    }

    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
        if (isSensitiveKey(key)) {
            out[key] = REDACTED;
        }
        else {
            out[key] = redactValue(v, depth + 1, visited);
        }
    }
    return out;
}

/**
 * Redact a single console argument. Strings get pattern-redacted in place;
 * objects/arrays are deep-walked. Other primitives pass through unchanged.
 *
 * This is the entry point used by consoleRecordPlugin.serializeArg.
 */
export function redactConsoleArg(arg: unknown): unknown {
    if (typeof arg === 'string') return redactString(arg);
    if (arg === null || typeof arg !== 'object') return arg;
    return redactValue(arg);
}

/** Internal exports for unit testing — do not use from production code. */
export const __test = {
    REDACTED,
    SENSITIVE_KEY_PATTERNS,
    JWT_RE,
    LONG_DIGIT_RE,
};
